import React, { useState, useContext, useRef, useEffect } from "react";
import Logoicon from "../assets/images/cloudVantage Logo.png";
import WhiteLogoicon from "../assets/images/cloudVantage Logo White.png";
import { Link, useLocation } from "react-router-dom";
import user from "../assets/svg-icons/user.svg";
import ThemeToggler from "./themeToggler";
import { ThemeContext } from "./themeContext";
import { HashLink } from "react-router-hash-link";
import {
  Boxes,
  Building2,
  FileText,
  Phone,
  LifeBuoy,
  CircleHelp,
  Info,
  BookText,
  Scale,
  ChevronDown,
  Server,
  Sun,
  Moon,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";

const Navbar = () => {
  const location = useLocation();
  const { theme, setTheme } = useContext(ThemeContext);
  const [openGroup, setOpenGroup] = useState(null);
  const navRef = useRef(null);

  const legalLinks = [
    { type: "link", to: "/privacy-policy", label: "Privacy Policy", icon: FileText, description: "How we collect and protect your data." },
    { type: "link", to: "/sla", label: "SLA", icon: FileText, description: "Service uptime commitments and support scope." },
    { type: "link", to: "/cloud-services-agreement", label: "Cloud Services Agreement", icon: FileText, description: "Terms for using our cloud platform services." },
    { type: "link", to: "/acceptable-use-policy", label: "Acceptable Use Policy", icon: FileText, description: "Rules and restrictions for platform usage." },
    { type: "link", to: "/terms-of-use", label: "Terms of Use", icon: FileText, description: "General terms governing use of this website." },
    { type: "link", to: "/data-processing", label: "Data Processing", icon: FileText, description: "How customer data is processed and handled." },
    { type: "link", to: "/cookie-policy", label: "Cookie Policy", icon: FileText, description: "Cookie usage and preference management." },
  ];

  const navGroups = {
    solutions: {
      label: "Solutions",
      icon: Boxes,
      items: [
        { type: "link", to: "/products", label: "Products", icon: Boxes, description: "Explore our cloud and infrastructure offerings." },
        { type: "link", to: "/blog", label: "Blog", icon: BookText, description: "Insights, updates, and practical cloud guides." },
        {
          type: "external",
          href: "https://docs.cloudvantage.com.ng",
          label: "Docs",
          icon: BookText,
          description: "Developer docs, APIs, and integration guides.",
        },
      ],
    },
    company: {
      label: "Company",
      icon: Building2,
      items: [
        { type: "link", to: "/about-us", label: "About us", icon: Info, description: "Learn about our mission and company story." },
        { type: "link", to: "/contact", label: "Contact us", icon: Phone, description: "Talk to sales or reach our support team." },
        { type: "link", to: "/support", label: "Support", icon: LifeBuoy, description: "Get help with account and technical issues." },
        { type: "hash", to: "/#faq", label: "FAQs", icon: CircleHelp, description: "Quick answers to frequently asked questions." },
      ],
    },
    legal: {
      label: "Legal",
      icon: Scale,
      items: legalLinks,
    },
  };

  const isItemActive = (item) => {
    if (item.type === "hash") return false;
    if (item.type === "external") return false;
    if (item.to === "/blog") return location.pathname.startsWith("/blog");
    return location.pathname === item.to;
  };

  const isGroupActive = (group) =>
    group.items.some((item) => isItemActive(item));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenGroup(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

    useEffect(() => {
      setOpenGroup(null);
    }, [location.pathname]);

    const closeMenu = () => {
      setOpenGroup(null);
    };

  const renderMenuItem = (item, isMobile = false) => {
    const ItemIcon = item.icon || FileText;
    const sharedClass = `flex items-start gap-3 rounded-md px-3 py-2 transition-all ${
      isItemActive(item)
        ? "text-primaryBlue font-semibold bg-primaryBlue/10"
        : "hover:bg-primaryBlue/5"
    }`;
    const textWrapClass = isMobile ? "leading-tight" : "leading-tight";
    const descriptionClass = "text-[11px] text-black/55 dark:text-white/60 font-normal";
    const iconWrap = (
      <span className="h-8 w-8 rounded-full bg-primaryBlue/10 border border-primaryBlue/20 flex items-center justify-center mt-0.5 shrink-0">
        <ItemIcon className="h-4 w-4 text-[#1DA1F2]" />
      </span>
    );
    const contentWrap = (
      <span className={`flex flex-col ${textWrapClass}`}>
        <span>{item.label}</span>
        {item.description && <span className={descriptionClass}>{item.description}</span>}
      </span>
    );

    if (item.type === "external") {
      return (
        <a href={item.href} onClick={closeMenu} className={sharedClass}>
          {iconWrap}
          {contentWrap}
        </a>
      );
    }

    if (item.type === "hash") {
      return (
        <HashLink smooth to={item.to} onClick={closeMenu} className={sharedClass}>
          {iconWrap}
          {contentWrap}
        </HashLink>
      );
    }

    return (
      <Link to={item.to} onClick={closeMenu} className={sharedClass}>
        {iconWrap}
        {contentWrap}
      </Link>
    );
  };

  return (
    <div className="dark:text-white fixed md:sticky top-0 z-40 w-full">
  <div className="flex xl:hidden items-center bg-white py-6 dark:bg-[#141D26] px-4 md:px-10 xl:px-[100px] border-b dark:border-[#c4c4c432]">
    <a href="https://www.cloudvantage.com.ng">
      <img
        src={theme === "dark" ? WhiteLogoicon : Logoicon}
        alt="CloudVantage"
        className="xl:hidden h-[18px]"
      />
    </a>
  </div>

      <div className="hidden xl:grid xl:grid-cols-[auto,1fr,auto] gap-8 lg:gap-10 items-center bg-white border-b dark:border-[#c4c4c432] dark:bg-[#141D26] px-[100px] py-4" >
        <div className="hidden xl:block pt-1">
         <a href="https://www.cloudvantage.com.ng">
          <img
            src={theme === "dark" ? WhiteLogoicon : Logoicon}
            alt="CloudVantage"
            className="h-[18px]"
          />
        </a>
        </div>

        <div className="grid xl:contents gap-6 xl:gap-8 items-start xl:items-center w-full" ref={navRef}>
    
          <div className="grid md:grid-cols-2 xl:flex gap-3 xl:gap-2 items-center xl:justify-self-end">

            {/* Desktop: icon cluster — docs + theme toggle */}
            <div className="hidden xl:flex items-center gap-1">
              <a
                href="https://docs.cloudvantage.com.ng"
                onClick={closeMenu}
                title="Docs"
                className="flex items-center gap-1 w-fit py-2 lg:py-3 px-3 lg:px-4 justify-center text-sm rounded-lg text-black/60 dark:text-white/60 hover:bg-primaryBlue/10 transition-colors"
              >
                <span>CloudVantage Docs</span>
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <button
                onClick={() => setTheme((p) => (p === "dark" ? "light" : "dark"))}
                title="Toggle theme"
                className="flex items-center justify-center size-9 bg-primaryBlue/5 rounded-lg hover:bg-primaryBlue/15 transition-colors"
              >
                {theme === "dark" ? <Sun className="h-[18px] w-[18px] text-primaryBlue" /> : <Moon className="h-[18px] w-[18px] text-primaryBlue" />}
              </button>
            </div>

            {/* Desktop divider */}
            <div className="hidden xl:block w-px h-5 bg-black/15 dark:bg-white/15 mx-1.5" />

            {/* Log in */}
            <Link
              to="/sign-in"
              onClick={closeMenu}
              className="flex items-center justify-center rounded font-medium lg:text-[#1DA1F2] border border-[#1DA1F2]/30 bg-primaryBlue/5 hover:bg-primaryBlue/15 hover:border-[#1DA1F2]/60 transition-all py-2 lg:py-3 px-3 lg:px-4 text-sm"
            >
              Log in
            </Link>

            {/* Spin up a VM */}
            <Link
              to="/create-account"
              onClick={closeMenu}
              className="flex items-center justify-center rounded text-white bg-[#1DA1F2] hover:bg-blue-500 transition-all py-2 lg:py-3 px-3 lg:px-4 text-sm"
            >
              <span>Spin up a VM</span>
            </Link>

            {/* Mobile: theme toggler */}
            <div className="xl:hidden px-1">
              <ThemeToggler />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;